import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  AddIcon,
  Button,
  DateRangePicker,
  Divider,
  ExcelIcon,
  Form,
  Input,
  Pagination,
  RefreshIcon,
  Select,
  Text,
  TextButton,
  type TypeSelectOption,
  dialogService,
  toastService,
} from '@thefarmersfront/kitchen';

import { useQueryClient } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';

import { PageTitle, Table, TableTopField } from '~/components/common';
import { PageWrapper } from '~/components/layout';
import {
  ProductDetailsModal,
  ProductMenuCodeProvider,
  ProductRegisterExcelModal,
  ProductRegisterModal,
} from '~/components/product';

import { useDownloadReady, useModal, useStableCallback, useToggle } from '~/hooks/common';
import {
  productKeys,
  useGetCategoriesCodesParentCodeQuery,
  useGetCategoriesTypesTypeQuery,
  useGetCodesMenuMenuCodeQuery,
  useGetOrderTypeInfoQuery,
  useGetSkusQuery,
  usePostSkusMutation,
  usePostThirdPartySkuExcelDownloadMutation,
  usePutSkusDisableMutation,
  usePutThirdPartiesSkusMutation,
} from '~/hooks/product';
import { useProductStore, useUserInfoStore } from '~/store';
import {
  focusFirstErrorField,
  formatDate,
  getDateRangeInfo,
  getProductRegisterColumnDefs,
  renderFilterErrorMessage,
  transformToProductSelectOptions as transformToSelectOptions,
  validateProductFields,
} from '~/utils';

import { BREAD_CRUMB, DEFAULT_INPUT_STATUS, OPTION_ALL, PATH, TOAST_MESSAGE } from '~/constants';
import {
  INITIAL_PRODUCT_PAGE_OFFSET,
  INITIAL_PRODUCT_PAGE_SIZE,
  PRODUCT_PAGE_SIZE,
  PRODUCT_USAGE_STATUS,
  PRODUCT_VALIDATION_MESSAGE,
  TypeProductPageSize,
} from '~/constants/product';

import type { TypeErrorResponseData, TypeInputState } from '~/types/common.types';
import type {
  TypeGetSkusParams,
  TypeProduct,
  TypeProductModalInput,
  TypeProductRegisterFilter,
  TypeProductRegisterPageCodesMenu,
} from '~/types/product.types';

import styles from './ProductRegisterPage.module.scss';

const INITIAL_PRODUCT_REGISTER_FILTER: TypeProductRegisterFilter = {
  skuActiveType: null,
  skuStatusType: null,
  skuKeyword: null,
  keywordValue: '',
  fromDate: null,
  toDate: null,
  mainCategoryCode: OPTION_ALL,
  middleCategoryCode: OPTION_ALL,
  thirdCategoryCode: OPTION_ALL,
  fourthCategoryCode: OPTION_ALL,
  productUsageStatus: OPTION_ALL,
  limit: INITIAL_PRODUCT_PAGE_SIZE,
  cursor: INITIAL_PRODUCT_PAGE_OFFSET,
};

const ProductRegisterPage = () => {
  const location = useLocation();

  const [ownerType, hasPermission] = useUserInfoStore(
    useShallow((state) => [state.userInfo.ownerType, state.hasPermission])
  );
  const hasPermission3PL = hasPermission([{ ownerType: '3PL' }]);
  const hasPermissionFBK = hasPermission([{ ownerType: 'FBK' }]);

  const [
    productRegisterModalValue,
    resetProductRegisterModalValue,
    productModalRequiredValueStatus,
    setProductModalRequiredValueStatus,
    resetProductModalRequiredValueStatus,
    productModalSkuCodeStatus,
    setProductModalSkuCodeStatus,
    resetProductModalSkuCodeStatus,
    productDetailModalValue,
    focusFieldRefs,
    resetFocusFieldRefs,
  ] = useProductStore(
    useShallow((state) => [
      state.productRegisterModalValue,
      state.resetProductRegisterModalValue,
      state.productModalRequiredValueStatus,
      state.setProductModalRequiredValueStatus,
      state.resetProductModalRequiredValueStatus,
      state.productModalSkuCodeStatus,
      state.setProductModalSkuCodeStatus,
      state.resetProductModalSkuCodeStatus,
      state.productDetailModalValue,
      state.focusFieldRefs,
      state.resetFocusFieldRefs,
    ])
  );

  const queryClient = useQueryClient();

  const codesMenuQuery = useGetCodesMenuMenuCodeQuery<TypeProductRegisterPageCodesMenu>({ menuCode: 'SKU_REG' });
  const mainCategoryQuery = useGetCategoriesTypesTypeQuery({
    type: 'MAIN',
    showGlobalErrorToast: false,
    showGlobalDimOverlay: false,
  });
  const orderTypeInfoQuery = useGetOrderTypeInfoQuery({ showGlobalErrorToast: false });
  const registerSkusMutation = usePostSkusMutation();
  const updateSkusMutation = usePutThirdPartiesSkusMutation();
  const deleteSkusMutation = usePutSkusDisableMutation();
  const excelDownloadMutation = usePostThirdPartySkuExcelDownloadMutation();

  const optionSkuKeyword = useMemo(
    () => (codesMenuQuery.data ? transformToSelectOptions(codesMenuQuery.data.skuKeyword.codeItemList) : []),
    [codesMenuQuery.data]
  );
  const optionSkuStatusType = useMemo(
    () => (codesMenuQuery.data ? transformToSelectOptions(codesMenuQuery.data.skuStatusType.codeItemList) : []),
    [codesMenuQuery.data]
  );
  const optionSkuActiveType = useMemo(
    () => (codesMenuQuery.data ? transformToSelectOptions(codesMenuQuery.data.skuActiveType.codeItemList) : []),
    [codesMenuQuery.data]
  );
  const optionProductUsageStatus = useMemo<TypeSelectOption<string>[]>(() => {
    if (!codesMenuQuery.data) return [];
    return hasPermissionFBK
      ? [OPTION_ALL, ...transformToSelectOptions(codesMenuQuery.data.productUsageStatus.codeItemList)]
      : [OPTION_ALL, ...Object.values(PRODUCT_USAGE_STATUS)];
  }, [codesMenuQuery.data, hasPermissionFBK]);

  const [listQueryParams, setListQueryParams] = useState<TypeGetSkusParams | null>(null);
  const skusQuery = useGetSkusQuery({
    params: listQueryParams ?? ({} as TypeGetSkusParams),
    enabled: !!listQueryParams,
  });

  const [selectedData, setSelectedData] = useState<TypeProduct[] | null>(null);
  const [selectedSkuCode, setSelectedSkuCode] = useState<string | null>(null);

  const [productRegisterFilter, setProductRegisterFilter] = useState<TypeProductRegisterFilter>(() => ({
    ...INITIAL_PRODUCT_REGISTER_FILTER,
    skuKeyword: optionSkuKeyword[0] ?? null,
    skuStatusType: optionSkuStatusType[0] ?? null,
    skuActiveType: optionSkuActiveType[0] ?? null,
  }));

  const { isDownloadReady, searchedFilter, markDownloadReady, checkFilterChange } = useDownloadReady({
    initialFilter: INITIAL_PRODUCT_REGISTER_FILTER,
  });

  const [hasKeywordInputError, setHasKeywordInputError] = useState(false);

  const pageOptions = Object.values(PRODUCT_PAGE_SIZE);

  const { isToggle: isFilterOpen, handleToggle: handleFilterToggle } = useToggle({ initialState: true });

  const handleSearch = useCallback(
    (filter: TypeProductRegisterFilter) => {
      setProductRegisterFilter(filter);

      const params: TypeGetSkusParams = {
        keyWordValue: filter.keywordValue,
        keyWord: filter.skuKeyword?.value ?? '',
        status: filter.skuStatusType?.value ?? '',
        isActive: filter.skuActiveType?.value === 'true',
        mainCategoryCode: filter.mainCategoryCode.value,
        middleCategoryCode: filter.middleCategoryCode.value,
        thirdCategoryCode: filter.thirdCategoryCode.value,
        fourthCategoryCode: filter.fourthCategoryCode.value,
        fromDate: formatDate(filter.fromDate, 'YYYY.MM.DD') ?? '',
        toDate: formatDate(filter.toDate, 'YYYY.MM.DD') ?? '',
        useMethodCode: filter.productUsageStatus.value,
        limit: filter.limit,
        cursor: filter.cursor,
      };
      queryClient.invalidateQueries({ queryKey: productKeys.skus(params) });
      setListQueryParams(params);
    },
    [queryClient]
  );

  const productRegisterModal = useModal({
    onOpen: () => {
      resetProductModalRequiredValueStatus();
      resetProductModalSkuCodeStatus();
    },
    onClose: () => {
      resetProductRegisterModalValue();
      resetFocusFieldRefs();
    },
    onConfirm: () => handleSearch(productRegisterFilter),
  });

  const productDetailModal = useModal({
    onOpen: () => {
      resetProductModalRequiredValueStatus();
      setProductModalSkuCodeStatus({
        isValid: true,
        validationMessage: '',
        status: 'default',
      });
    },
    onCloseCompleted: () => {
      resetFocusFieldRefs();
      setSelectedSkuCode(null);
    },
    onConfirm: () => handleSearch(productRegisterFilter),
  });

  const productExcelRegisterModal = useModal({ onConfirm: () => handleSearch(productRegisterFilter) });

  useEffect(() => {
    if (!codesMenuQuery.data) return;

    setProductRegisterFilter((prev) => ({
      ...prev,
      skuStatusType: prev.skuStatusType ?? optionSkuStatusType[0] ?? null,
      skuActiveType: prev.skuActiveType ?? optionSkuActiveType[0] ?? null,
      skuKeyword: prev.skuKeyword ?? optionSkuKeyword[0] ?? null,
    }));
  }, [codesMenuQuery.data, optionSkuActiveType, optionSkuKeyword, optionSkuStatusType]);

  const handleOpenProductDetailModal = useStableCallback((skuCode?: string) => {
    if (!skuCode) return;

    setSelectedSkuCode(skuCode);
    productDetailModal.handleOpenModal();
  });

  useEffect(() => {
    // '운영 상품 조회' 페이지에서 넘어온 상품 코드가 있으면 모달 열기
    const stateData = location.state as { targetSkuCode?: string } | null;
    const skuCode = stateData?.targetSkuCode;

    if (skuCode && codesMenuQuery.data) {
      handleOpenProductDetailModal(skuCode);
      window.history.replaceState(null, '', location.pathname);
    }
  }, [location.state, codesMenuQuery.data]);

  const handleFilterChange = useStableCallback(() => checkFilterChange(productRegisterFilter));

  useEffect(() => {
    handleFilterChange();
  }, [productRegisterFilter, handleFilterChange]);

  const hasFilterError = codesMenuQuery.isError || orderTypeInfoQuery.isError || mainCategoryQuery.isError;

  useEffect(() => {
    if (hasFilterError) {
      toastService.addToast({ type: 'error', persist: true, message: renderFilterErrorMessage() });
    }
  }, [hasFilterError]);

  const mainCategoryOptions = mainCategoryQuery.data ? transformToSelectOptions(mainCategoryQuery.data) : [];

  // 중분류 카테고리 조회 (대분류 선택 시)
  const selectedMainCode = productRegisterFilter.mainCategoryCode.value;
  const middleCategoryQuery = useGetCategoriesCodesParentCodeQuery({
    parentCode: selectedMainCode !== OPTION_ALL.value ? selectedMainCode : null,
    showGlobalDimOverlay: false,
  });

  // 소분류 카테고리 조회 (중분류 선택 시)
  const selectedMiddleCode = productRegisterFilter.middleCategoryCode.value;
  const thirdCategoryQuery = useGetCategoriesCodesParentCodeQuery({
    parentCode: selectedMiddleCode !== OPTION_ALL.value ? selectedMiddleCode : null,
    showGlobalDimOverlay: false,
  });

  // 세분류 카테고리 조회 (소분류 선택 시)
  const selectedThirdCode = productRegisterFilter.thirdCategoryCode.value;
  const fourthCategoryQuery = useGetCategoriesCodesParentCodeQuery({
    parentCode: selectedThirdCode !== OPTION_ALL.value ? selectedThirdCode : null,
    showGlobalDimOverlay: false,
  });

  const optionMiddleCategory = useMemo(() => {
    if (!middleCategoryQuery.data) return [];
    return [OPTION_ALL, ...transformToSelectOptions(middleCategoryQuery.data)];
  }, [middleCategoryQuery.data]);

  const optionThirdCategory = useMemo(() => {
    if (!thirdCategoryQuery.data) return [];
    return [OPTION_ALL, ...transformToSelectOptions(thirdCategoryQuery.data)];
  }, [thirdCategoryQuery.data]);

  const optionFourthCategory = useMemo(() => {
    if (!fourthCategoryQuery.data) return [];
    return [OPTION_ALL, ...transformToSelectOptions(fourthCategoryQuery.data)];
  }, [fourthCategoryQuery.data]);

  const onSkusLoaded = useStableCallback(() => {
    if (!skusQuery.data) return;
    markDownloadReady({ filterValue: productRegisterFilter, hasData: skusQuery.data.data.length > 0 });
  });

  useEffect(() => {
    onSkusLoaded();
  }, [skusQuery.data, onSkusLoaded]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    handleSearch({
      ...productRegisterFilter,
      cursor: INITIAL_PRODUCT_PAGE_OFFSET,
    });
  };

  const handleChangeManageTypeSelect = (_: unknown, option: TypeSelectOption<string>) => {
    setProductRegisterFilter({
      ...productRegisterFilter,
      skuActiveType: option,
    });
  };

  const handleChangeRegisterStatusSelect = (_: unknown, option: TypeSelectOption<string>) => {
    setProductRegisterFilter({
      ...productRegisterFilter,
      skuStatusType: option,
    });
  };

  const handleChangeKeywordSelect = (_: unknown, option: TypeSelectOption<string>) => {
    setProductRegisterFilter({
      ...productRegisterFilter,
      skuKeyword: option,
      keywordValue: '',
    });
  };

  const handleClickKeywordValueInput = () => {
    setHasKeywordInputError(false);
  };

  const handleChangeKeywordValueInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProductRegisterFilter({
      ...productRegisterFilter,
      keywordValue: e.target.value,
    });
  };

  const handleChangeRegisterDatePicker = (selectedDate: { start: Date | null; end: Date | null } | null) => {
    const startDate = selectedDate?.start;
    const endDate = selectedDate?.end;

    setProductRegisterFilter({
      ...productRegisterFilter,
      fromDate: startDate,
      toDate: endDate,
    });
  };

  useEffect(() => {
    setProductRegisterFilter({
      ...productRegisterFilter,
      middleCategoryCode: OPTION_ALL,
      thirdCategoryCode: OPTION_ALL,
      fourthCategoryCode: OPTION_ALL,
    });
  }, [productRegisterFilter.mainCategoryCode]);

  useEffect(() => {
    setProductRegisterFilter({
      ...productRegisterFilter,
      thirdCategoryCode: OPTION_ALL,
      fourthCategoryCode: OPTION_ALL,
    });
  }, [productRegisterFilter.middleCategoryCode]);

  useEffect(() => {
    setProductRegisterFilter({
      ...productRegisterFilter,
      fourthCategoryCode: OPTION_ALL,
    });
  }, [productRegisterFilter.thirdCategoryCode]);

  const handleChangeMainCategorySelect = (_: unknown, option: TypeSelectOption<string>) => {
    if (option === productRegisterFilter.mainCategoryCode) {
      return;
    }

    setProductRegisterFilter({
      ...productRegisterFilter,
      mainCategoryCode: option,
    });
  };

  const handleChangeMiddleCategorySelect = (_: unknown, option: TypeSelectOption<string>) => {
    if (option === productRegisterFilter.middleCategoryCode) {
      return;
    }

    setProductRegisterFilter({
      ...productRegisterFilter,
      middleCategoryCode: option,
    });
  };

  const handleChangeThirdCategorySelect = (_: unknown, option: TypeSelectOption<string>) => {
    if (option === productRegisterFilter.thirdCategoryCode) {
      return;
    }

    setProductRegisterFilter({
      ...productRegisterFilter,
      thirdCategoryCode: option,
    });
  };

  const handleChangeFourthCategorySelect = (_: unknown, option: TypeSelectOption<string>) => {
    setProductRegisterFilter({
      ...productRegisterFilter,
      fourthCategoryCode: option,
    });
  };

  const handleChangeProductUsageStatusSelect = (_: unknown, option: TypeSelectOption<string>) => {
    setProductRegisterFilter({
      ...productRegisterFilter,
      productUsageStatus: option,
    });
  };

  const handleClickReset = () => {
    setProductRegisterFilter({
      ...INITIAL_PRODUCT_REGISTER_FILTER,
      skuStatusType: optionSkuStatusType?.[0] ?? null,
      skuActiveType: optionSkuActiveType?.[0] ?? null,
      skuKeyword: optionSkuKeyword?.[0] ?? null,
    });
  };

  const handleChangePagesizeSelect = (_: unknown, { value }: TypeSelectOption<TypeProductPageSize>) => {
    handleSearch({
      ...productRegisterFilter,
      limit: value,
      cursor: 0,
    });
  };

  const handleClickPageButton = (e: React.MouseEvent<HTMLButtonElement>) => {
    const newPage = Number(e.currentTarget.value);

    handleSearch({
      ...productRegisterFilter,
      cursor: newPage - 1,
    });
  };

  const handleChangeTableSelection = (selectedRows: TypeProduct[]) => {
    setSelectedData(selectedRows);
  };

  const handleClickProductDelete = () => {
    if (!selectedData?.length) return;

    if (searchedFilter.skuActiveType?.value === 'true') {
      dialogService.addDialog({
        children: '등록 완료된 상품은 삭제할 수 없습니다.',
      });
      return;
    }

    dialogService.addDialog({
      type: 'confirm',
      children: '선택한 상품을 삭제하시겠습니까?',
      confirmText: '삭제',
      onConfirm: () =>
        deleteSkusMutation.mutate(
          {
            skuIds: selectedData?.map((item) => item.id) ?? [],
            deleted: true,
          },
          {
            onSuccess: () => {
              toastService.addToast({
                message: '상품이 삭제되었습니다.',
              });
              handleSearch(productRegisterFilter);
            },
          }
        ),
    });
  };

  const handleExcelDownload = () => {
    excelDownloadMutation.mutate(
      {
        keyWord: String(searchedFilter.skuKeyword?.value ?? ''),
        keyWordValue: searchedFilter.keywordValue,
        status: String(searchedFilter.skuStatusType?.value ?? ''),
        isActive: searchedFilter.skuActiveType?.value === 'true',
        mainCategoryCode: String(searchedFilter.mainCategoryCode.value),
        middleCategoryCode: String(searchedFilter.middleCategoryCode.value),
        thirdCategoryCode: String(searchedFilter.thirdCategoryCode.value),
        fourthCategoryCode: String(searchedFilter.fourthCategoryCode.value),
        fromDate: formatDate(searchedFilter.fromDate, 'YYYY.MM.DD'),
        toDate: formatDate(searchedFilter.toDate, 'YYYY.MM.DD'),
      },
      {
        onSuccess: () => {
          toastService.addToast({ message: TOAST_MESSAGE.DOWNLOAD_SUCCESS });
        },
      }
    );
  };

  const handleClickExcelFormDownload = async () => {
    try {
      if (ownerType === null) {
        throw new Error('화주사 타입을 확인할 수 없습니다.');
      }

      const downloadFormLink = hasPermission3PL
        ? process.env.PRODUCT_3PL_EXCEL_FORM_URL
        : process.env.PRODUCT_FBK_EXCEL_FORM_URL;

      if (!downloadFormLink) {
        throw new Error('엑셀 양식 다운로드 링크를 찾을 수 없습니다.');
      }

      window.open(downloadFormLink, '_blank', 'noopener,noreferrer');
    } catch (error) {
      toastService.addToast({
        type: 'error',
        message: (error as TypeErrorResponseData['error'])?.message || TOAST_MESSAGE.DEFAULT_ERROR,
      });
    }
  };

  const validateInputValues = (
    inputValues: TypeProductModalInput
  ): {
    updatedState: Record<string, TypeInputState>;
    isValid: boolean;
  } => {
    // 에러 상태 초기화
    const initialStatus = Object.keys(productModalRequiredValueStatus).reduce(
      (acc, key) => ({ ...acc, [key]: DEFAULT_INPUT_STATUS }),
      {}
    );

    // SKU 코드 검증
    const validateSkuCode = (): boolean => {
      if (hasPermission3PL) return true;

      if (!inputValues.skuCode) {
        setProductModalSkuCodeStatus({
          ...productModalSkuCodeStatus,
          isValid: false,
          validationMessage: PRODUCT_VALIDATION_MESSAGE.REQUIRED_INPUT,
          status: 'error',
        });
        return false;
      }

      if (inputValues.skuCode && !productModalSkuCodeStatus.isValid) {
        setProductModalSkuCodeStatus({
          ...productModalSkuCodeStatus,
          validationMessage: PRODUCT_VALIDATION_MESSAGE.SKU_CODE_SEARCH_NEEDED,
          status: 'error',
        });
        return false;
      }

      return true;
    };

    // 상품 필드 검증
    const updatedState = validateProductFields(inputValues, hasPermissionFBK ? 'FBK' : '3PL', initialStatus);

    // SKU 코드 검증
    const isSkuCodeValid = validateSkuCode();

    // 전체 검증 결과
    const hasErrors = Object.values(updatedState).some((status) => status.status === 'error') || !isSkuCodeValid;

    return {
      updatedState,
      isValid: !hasErrors,
    };
  };

  const handleConfirmRegisterModal = () => {
    const { isValid, updatedState } = validateInputValues(productRegisterModalValue);

    setProductModalRequiredValueStatus({ ...updatedState });

    if (isValid) {
      registerSkusMutation.mutate(
        {
          ...productRegisterModalValue,
          supplierCodes: productRegisterModalValue.supplierCodes?.map(({ value }) => value) ?? [],
          shelfLifeType: productRegisterModalValue.shelfLifeType?.value ?? '',
          temperatureType: productRegisterModalValue.temperatureType?.value ?? '',
          mainCategoryCode: productRegisterModalValue.mainCategoryCode?.value ?? '',
          middleCategoryCode: productRegisterModalValue.middleCategoryCode?.value ?? '',
          thirdCategoryCode: productRegisterModalValue.thirdCategoryCode?.value ?? '',
          fourthCategoryCode: productRegisterModalValue.fourthCategoryCode?.value ?? '',
          unit: productRegisterModalValue.unit?.value ?? '',
          productType: productRegisterModalValue.productType?.value ?? '',
          kurlyOrderType: productRegisterModalValue.kurlyOrderType?.value ?? '',
          parcelOrderType: productRegisterModalValue.parcelOrderType?.value ?? '',
          massDocumentNum: productRegisterModalValue.massDocumentNum?.value ?? '',
          kurlyKeepType: productRegisterModalValue.kurlyKeepType?.value ?? '',
          deliveryDocumentNum: productRegisterModalValue.deliveryDocumentNum?.value ?? '',
          packingDirection: productRegisterModalValue.packingDirection?.value ?? '',
          sellingType: productRegisterModalValue.sellingType,
          stockSafeDate: productRegisterModalValue.stockSafeDate
            ? Number(productRegisterModalValue.stockSafeDate)
            : null,
        },
        {
          onSuccess: () => {
            resetProductModalRequiredValueStatus();
            resetProductModalSkuCodeStatus();
            resetProductRegisterModalValue();

            toastService.addToast({
              message: '상품 정보가 등록 요청되었습니다.\n관리자의 확인 후 정식 등록처리 됩니다.',
            });

            productRegisterModal.handleConfirmModal();
            resetFocusFieldRefs();
          },
        }
      );
    } else {
      focusFirstErrorField(focusFieldRefs, updatedState);
    }
  };

  const handleConfirmDetailModal = () => {
    const { isValid, updatedState } = validateInputValues(productDetailModalValue);

    setProductModalRequiredValueStatus({ ...updatedState });

    if (isValid) {
      updateSkusMutation.mutate(
        {
          ...productDetailModalValue,
          supplierCodes: productDetailModalValue.supplierCodes?.map(({ value }) => value) ?? [],
          shelfLifeType: productDetailModalValue.shelfLifeType?.value ?? '',
          temperatureType: productDetailModalValue.temperatureType?.value ?? '',
          mainCategoryCode: productDetailModalValue.mainCategoryCode?.value ?? '',
          middleCategoryCode: productDetailModalValue.middleCategoryCode?.value ?? '',
          thirdCategoryCode: productDetailModalValue.thirdCategoryCode?.value ?? '',
          fourthCategoryCode: productDetailModalValue.fourthCategoryCode?.value ?? '',
          unit: productDetailModalValue.unit?.value ?? '',
          productType: productDetailModalValue.productType?.value ?? '',
          kurlyOrderType: productDetailModalValue.kurlyOrderType?.value ?? '',
          parcelOrderType: productDetailModalValue.parcelOrderType?.value ?? '',
          massDocumentNum: productDetailModalValue.massDocumentNum?.value ?? '',
          kurlyKeepType: productDetailModalValue.kurlyKeepType?.value ?? '',
          deliveryDocumentNum: productDetailModalValue.deliveryDocumentNum?.value ?? '',
          packingDirection: productDetailModalValue.packingDirection?.value ?? '',
          sellingType: productDetailModalValue.sellingType,
          stockSafeDate: productDetailModalValue.stockSafeDate ? Number(productDetailModalValue.stockSafeDate) : null,
          useMethodCode: productDetailModalValue.useMethodCode,
        },
        {
          onSuccess: () => {
            resetProductModalRequiredValueStatus();
            resetProductModalSkuCodeStatus();

            toastService.addToast({
              message: '상품 정보가 등록 요청되었습니다.\n관리자의 확인 후 정식 등록처리 됩니다.',
            });

            productDetailModal.handleConfirmModal();
            resetFocusFieldRefs();
          },
        }
      );
    } else {
      focusFirstErrorField(focusFieldRefs, updatedState);
    }
  };

  const renderTableTopRightButtons = () => {
    return (
      <>
        <Button
          size="tiny"
          status="error"
          variant="outlined"
          className={styles['table-top-field-button']}
          disabled={!selectedData?.length}
          onClick={handleClickProductDelete}
        >
          상품 삭제
        </Button>
        <Button
          size="tiny"
          leftIcon={<ExcelIcon size={16} />}
          name="excel-download"
          color="tertiary"
          className={styles['table-top-field-button']}
          disabled={!isDownloadReady}
          onClick={handleExcelDownload}
        >
          엑셀 다운로드
        </Button>
        <Divider orientation="vertical" className={styles['table-top-field-divider']} />
        <Button
          size="tiny"
          color="tertiary"
          name="excel-form-download"
          className={styles['table-top-field-button']}
          onClick={handleClickExcelFormDownload}
        >
          엑셀 양식 다운로드
        </Button>
        <Button
          size="tiny"
          color="tertiary"
          name="upload-products-via-excel"
          className={styles['table-top-field-button']}
          onClick={productExcelRegisterModal.handleOpenModal}
        >
          상품 엑셀 일괄 등록
        </Button>
        <Button
          size="tiny"
          leftIcon={<AddIcon size={16} />}
          name="upload-product"
          color="secondary"
          onClick={productRegisterModal.handleOpenModal}
        >
          상품 등록
        </Button>
      </>
    );
  };

  const { columnDefs } = useMemo(
    () => getProductRegisterColumnDefs({ onClickViewDetail: handleOpenProductDetailModal }),
    [handleOpenProductDetailModal]
  );

  return (
    <>
      <PageWrapper>
        <PageTitle
          title="상품 등록/수정"
          breadcrumbs={BREAD_CRUMB[PATH.PRODUCT_REGISTER]}
          handleFilterToggle={handleFilterToggle}
        />
        {isFilterOpen && (
          <Form className={styles['filter-section']} onSubmit={handleSubmit}>
            <div className={styles['filters-container']}>
              <div className={styles['filter-column-container']}>
                <div className={styles['filter-container']}>
                  <Text className={styles['filter-label']} weight="semi-bold" size="small">
                    상품 운영 구분
                  </Text>
                  <Select
                    placeholder="선택"
                    value={productRegisterFilter.skuActiveType}
                    options={optionSkuActiveType}
                    size="small"
                    onChange={handleChangeManageTypeSelect}
                  />
                </div>
                <div className={styles['filter-container']}>
                  <Text className={styles['filter-label']} weight="semi-bold" size="small">
                    상품 등록 상태
                  </Text>
                  <Select
                    placeholder="선택"
                    value={productRegisterFilter.skuStatusType}
                    options={optionSkuStatusType}
                    size="small"
                    onChange={handleChangeRegisterStatusSelect}
                  />
                </div>
              </div>
              <div className={styles['filter-container']}>
                <Text className={styles['filter-label']} weight="semi-bold" size="small">
                  키워드
                </Text>
                <div className={styles['input-container']}>
                  <Select
                    className={styles['input-select']}
                    placeholder="선택"
                    value={productRegisterFilter.skuKeyword}
                    options={optionSkuKeyword}
                    size="small"
                    onChange={handleChangeKeywordSelect}
                  />
                  <Input
                    type="text"
                    status={hasKeywordInputError ? 'error' : 'default'}
                    placeholder="입력"
                    value={productRegisterFilter.keywordValue}
                    size="small"
                    maxLength={200}
                    onClick={handleClickKeywordValueInput}
                    onChange={handleChangeKeywordValueInput}
                  />
                </div>
              </div>
              <div className={styles['filter-container']}>
                <Text className={styles['filter-label']} weight="semi-bold" size="small">
                  상품 등록일
                </Text>
                <DateRangePicker
                  size="small"
                  placeholder={{
                    start: '시작일',
                    end: '종료일',
                  }}
                  value={getDateRangeInfo(productRegisterFilter.fromDate, productRegisterFilter.toDate)}
                  allowSingleDateSelect
                  onChange={handleChangeRegisterDatePicker}
                />
              </div>
              <div className={styles['filter-column-container']}>
                <div className={styles['filter-container']}>
                  <Text className={styles['filter-label']} weight="semi-bold" size="small">
                    대분류
                  </Text>
                  <Select
                    placeholder="선택"
                    value={productRegisterFilter.mainCategoryCode}
                    options={[OPTION_ALL, ...mainCategoryOptions]}
                    size="small"
                    onChange={handleChangeMainCategorySelect}
                  />
                </div>
                <div className={styles['filter-container']}>
                  <Text className={styles['filter-label']} weight="semi-bold" size="small">
                    중분류
                  </Text>
                  <Select
                    placeholder="선택"
                    value={productRegisterFilter.middleCategoryCode}
                    options={optionMiddleCategory}
                    size="small"
                    disabled={!optionMiddleCategory.length}
                    onChange={handleChangeMiddleCategorySelect}
                  />
                </div>
              </div>
              <div className={styles['filter-column-container']}>
                <div className={styles['filter-container']}>
                  <Text className={styles['filter-label']} weight="semi-bold" size="small">
                    소분류
                  </Text>
                  <Select
                    placeholder="선택"
                    value={productRegisterFilter.thirdCategoryCode}
                    options={optionThirdCategory}
                    size="small"
                    disabled={!optionThirdCategory.length}
                    onChange={handleChangeThirdCategorySelect}
                  />
                </div>
                <div className={styles['filter-container']}>
                  <Text className={styles['filter-label']} weight="semi-bold" size="small">
                    세분류
                  </Text>
                  <Select
                    placeholder="선택"
                    value={productRegisterFilter.fourthCategoryCode}
                    options={optionFourthCategory}
                    size="small"
                    disabled={!optionFourthCategory.length}
                    onChange={handleChangeFourthCategorySelect}
                  />
                </div>
              </div>
              <div className={styles['filter-column-container']}>
                <div className={styles['filter-container']}>
                  <Text className={styles['filter-label']} weight="semi-bold" size="small">
                    상품 사용 여부
                  </Text>
                  <Select
                    placeholder="선택"
                    value={productRegisterFilter.productUsageStatus}
                    options={optionProductUsageStatus}
                    size="small"
                    onChange={handleChangeProductUsageStatusSelect}
                  />
                </div>
              </div>
            </div>
            <div className={styles['filter-actions']}>
              <div className={styles['filter-actions-left-field']} />
              <div className={styles['filter-actions-center-field']}>
                <Button type="submit" size="small">
                  검색
                </Button>
              </div>
              <div className={styles['filter-actions-right-field']}>
                <TextButton
                  className={styles['filter-reset-button']}
                  leftIcon={<RefreshIcon size={16} />}
                  size="small"
                  color="secondary"
                  onClick={handleClickReset}
                >
                  필터 초기화
                </TextButton>
              </div>
            </div>
          </Form>
        )}
        <div className={styles['view-section']}>
          <TableTopField
            dataTotalCount={skusQuery.data?.pagination.listTotal ?? 0}
            rightButton={renderTableTopRightButtons()}
          />
          <div className={styles['view-section-table-wrapper']}>
            <Table
              className={styles['view-table']}
              data={skusQuery.data?.data ?? null}
              columns={columnDefs}
              rowStatusType={!listQueryParams ? 'INIT' : 'EMPTY'}
              selection={{ onChange: handleChangeTableSelection }}
            />
          </div>
        </div>
        <div className={styles['view-actions-section']}>
          <div className={styles['view-options']}>
            <Select
              className={styles['limit-select']}
              options={pageOptions}
              value={PRODUCT_PAGE_SIZE[productRegisterFilter.limit]}
              size="tiny"
              dropdownPlacement="top-left"
              onChange={handleChangePagesizeSelect}
            />
          </div>
          {skusQuery.data?.pagination.listTotal ? (
            <Pagination
              className={styles['pagination']}
              currentPage={productRegisterFilter.cursor + 1}
              totalCount={skusQuery.data.pagination.listTotal}
              pageSize={productRegisterFilter.limit}
              onClick={handleClickPageButton}
            />
          ) : null}
        </div>
      </PageWrapper>
      <ProductMenuCodeProvider value="SKU_REG">
        {productRegisterModal.isOpen && (
          <ProductRegisterModal
            open={productRegisterModal.isOpen}
            onCancel={productRegisterModal.handleCloseModal}
            onConfirm={handleConfirmRegisterModal}
          />
        )}
        {productDetailModal.isOpen && selectedSkuCode && (
          <ProductDetailsModal
            skuCode={selectedSkuCode}
            open={productDetailModal.isOpen}
            onCancel={productDetailModal.handleCloseModal}
            onConfirm={handleConfirmDetailModal}
          />
        )}
      </ProductMenuCodeProvider>
      {productExcelRegisterModal.isOpen && (
        <ProductRegisterExcelModal
          open={productExcelRegisterModal.isOpen}
          onCancel={productExcelRegisterModal.handleCloseModal}
          onConfirm={productExcelRegisterModal.handleConfirmModal}
        />
      )}
    </>
  );
};

export default ProductRegisterPage;
